import { generateClient } from 'aws-amplify/api';
import React, { createContext } from 'react';
import { initiateBulkUpload } from '../graphql/mutations';
import {
  getBulkUploadConfiguration,
} from '../graphql/queries';
import { resolveBulkUploadErrorMessage } from './bulkUploadErrorUtils';

const BULK_UPLOAD_DETAILS_FRAGMENT = /* GraphQL */`
fragment BulkUploadDetailFields on BulkUploadDetails {
  id
  uploadDate
  status
  totalRowCount
  createdCount
  processingCompleteCount
  creationErrorCount
  processingErrorCount
  approvedCount
  rejectedCount
  otherCount
  cancelledCount
  errorSamples
  resultDownloadUrl
  resultFileName
}
`

const DEFAULT_LIST_LIMIT = 10;

const BULK_UPLOAD_LIST_FIELDS = `
  id
  fileName
  fileSize
  title
  description
  uploadDate
  s3Key
  status
  contentType
  uploadedBy
  totalRowCount
  errorCount
`;

const LIST_BULK_UPLOADS_QUERY = /* GraphQL */ `
query ListBulkUploads($input: ListBulkUploadsInput) {
  listBulkUploads(input: $input) {
    uploads {
      ${BULK_UPLOAD_LIST_FIELDS}
    }
    nextToken
  }
}
`;

const buildListInput = (listInput, nextToken) => {
  const input = { ...(listInput || {}) };
  if (input.limit === undefined || input.limit === null) {
    input.limit = DEFAULT_LIST_LIMIT;
  }
  if (nextToken) {
    input.nextToken = nextToken;
  } else if ('nextToken' in input) {
    delete input.nextToken;
  }
  if ('sortDirection' in input) {
    delete input.sortDirection;
  }
  return input;
};

const buildListBulkUploadsWithDetailsQuery = (detailUploadIds) => {
  const detailVariables = detailUploadIds
    .map((_, index) => `$detailId${index}: ID!`)
    .join(', ');
  const variableDefinitions = [
    '$input: ListBulkUploadsInput',
    detailVariables,
  ].filter(Boolean).join(', ');
  const detailSelections = detailUploadIds
    .map((_, index) => (
      `detail${index}: bulkUpload(id: $detailId${index}) { ...BulkUploadDetailFields }`
    ))
    .join('\n');
  const detailBlock = detailSelections ? `\n${detailSelections}` : '';
  const fragment = detailSelections ? BULK_UPLOAD_DETAILS_FRAGMENT : '';
  return `${fragment}\nquery ListBulkUploadsWithDetails(${variableDefinitions}) {\n  listBulkUploads(input: $input) {\n    uploads {\n      ${BULK_UPLOAD_LIST_FIELDS}\n    }\n    nextToken\n  }${detailBlock}\n}`;
};

const GET_BULK_UPLOAD_DETAILS_QUERY = /* GraphQL */`
${BULK_UPLOAD_DETAILS_FRAGMENT}
query GetBulkUploadDetails($id: ID!) {
  bulkUpload(id: $id) {
    ...BulkUploadDetailFields
  }
}
`;

const GENERATE_BULK_UPLOAD_ARCHIVE_MUTATION = /* GraphQL */ `
mutation GenerateBulkUploadArchive($id: ID!) {
  generateBulkUploadArchive(id: $id) {
    downloadUrl
    fileName
    expiresIn
  }
}
`;

const client = generateClient();

// Context for BulkUpload service
const BulkUploadContext = createContext();

// Provider component
export const BulkUploadContextProvider = ({ children }) => {
  // Upload a file
  const uploadFile = async ({
    file,
    title,
    description,
    uploadedBy,
    vowId,
    fileHash,
  }) => {
    try {
      const initiateInput = {
        fileName: file.name,
        contentType: file.type || 'application/octet-stream',
        title,
        description,
        uploadedBy,
        vowId,
      };
      if (fileHash) {
        initiateInput.fileHash = fileHash;
      }

      // Step 1: Initiate bulk upload to get presigned URL
      const initiateResponse = await client.graphql({
        query: initiateBulkUpload,
        authMode: 'userPool',
        variables: {
          input: initiateInput,
        },
      });

      const initiation = initiateResponse?.data?.initiateBulkUpload;
      if (!initiation) {
        throw new Error('Bulk upload initiation failed. Missing upload metadata in response.');
      }

      const { uploadUrl, uploadId, s3Key, errorCode, errorMessage } = initiation;
      if (errorCode || errorMessage) {
        const detail = [errorCode, errorMessage]
          .filter((value) => typeof value === 'string' && value.trim())
          .join(': ');
        throw new Error(detail ? `Bulk upload initiation failed: ${detail}` : 'Bulk upload initiation failed.');
      }

      if (!uploadUrl || !uploadId || !s3Key) {
        throw new Error('Bulk upload initiation failed. Missing upload URL or identifiers.');
      }

      // Step 2: Upload file to S3 using presigned URL
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type || 'application/octet-stream',
        },
        body: file,
      });

      if (!uploadResponse?.ok) {
        const statusCode = uploadResponse?.status;
        const statusText = uploadResponse?.statusText?.trim();
        const statusDetails = [statusCode, statusText].filter(Boolean).join(' ');
        throw new Error(
          statusDetails
            ? `Failed to upload file to S3 (${statusDetails}).`
            : 'Failed to upload file to S3.',
        );
      }

      // Step 3: Return upload data
      const uploadData = {
        id: uploadId,
        fileName: file.name,
        fileSize: file.size,
        title,
        description,
        uploadedBy,
        uploadDate: new Date().toISOString(),
        status: 'uploaded',
        s3Key,
      };

      return uploadData;
    } catch (error) {
      console.error('Error uploading file:', error);
      const message = resolveBulkUploadErrorMessage(error, { includeCauseMessage: true });
      if (error instanceof Error && error.message === message) {
        throw error;
      }
      throw new Error(message);
    }
  };

  // List uploaded files
  const listUploads = async () => {
    try {
      const uploads = [];
      let nextToken;

      do {
        const response = await client.graphql({
          query: LIST_BULK_UPLOADS_QUERY,
          authMode: 'userPool',
          variables: {
            input: buildListInput(null, nextToken),
          },
        });

        const listResult = response?.data?.listBulkUploads;
        if (Array.isArray(listResult?.uploads)) {
          uploads.push(...listResult.uploads);
        }
        nextToken = listResult?.nextToken;
      } while (nextToken);

      return uploads;
    } catch (error) {
      console.error('Error listing uploads:', error);
      throw error;
    }
  };

  const getConfiguration = async () => {
    try {
      const response = await client.graphql({
        query: getBulkUploadConfiguration,
        authMode: 'userPool',
      });

      const configuration = response?.data?.bulkUploadConfiguration;
      if (!configuration) {
        throw new Error('Bulk upload configuration missing from response.');
      }

      const vows = response?.data?.listVowConfigs;
      if (!vows || vows.length === 0) {
        throw new Error('No VOWs are configured for bulk upload.')
      }
      return { configuration, vows };
    } catch (error) {
      console.error('Error fetching bulk upload configuration:', error);
      throw error;
    }
  };

  const getBulkUploadDetails = async ({ uploadId }) => {
    if (!uploadId) {
      throw new Error('uploadId is required.');
    }

    try {
      const response = await client.graphql({
        query: GET_BULK_UPLOAD_DETAILS_QUERY,
        authMode: 'userPool',
        variables: {
          id: uploadId,
        },
      });

      const details = response?.data?.bulkUpload;
      if (!details) {
        throw new Error('Bulk upload details missing from response.');
      }

      return details;
    } catch (error) {
      console.error('Error fetching bulk upload details:', error);
      throw error;
    }
  };

  const listUploadsWithDetails = async ({
    listInput,
    detailUploadId,
    detailUploadIds,
    maxPages,
    maxItems,
  } = {}) => {
    const detailIds = [
      detailUploadId,
      ...(Array.isArray(detailUploadIds) ? detailUploadIds : []),
    ];
    const uniqueDetailIds = [...new Set(detailIds.filter(Boolean))];
    const maxPageCount = Number.isFinite(maxPages) ? Math.max(1, maxPages) : null;
    const maxItemCount = Number.isFinite(maxItems) ? Math.max(1, maxItems) : null;

    try {
      const uploads = [];
      let details = [];
      let nextToken = listInput?.nextToken;
      let nextTokenForReturn = nextToken ?? null;
      let isFirstPage = true;
      let pageCount = 0;

      do {
        const detailIdsForPage = isFirstPage ? uniqueDetailIds : [];
        const response = await client.graphql({
          query: buildListBulkUploadsWithDetailsQuery(detailIdsForPage),
          authMode: 'userPool',
          variables: detailIdsForPage.reduce((acc, id, index) => {
            acc[`detailId${index}`] = id;
            return acc;
          }, { input: buildListInput(listInput, nextToken) }),
        });

        const data = response?.data || {};
        const listResult = data?.listBulkUploads || {};
        if (Array.isArray(listResult.uploads)) {
          uploads.push(...listResult.uploads);
        }
        if (isFirstPage && detailIdsForPage.length > 0) {
          details = detailIdsForPage
            .map((_, index) => data[`detail${index}`])
            .filter(Boolean);
        }
        nextToken = listResult?.nextToken;
        nextTokenForReturn = nextToken ?? null;
        isFirstPage = false;
        pageCount += 1;
        if (maxItemCount && uploads.length >= maxItemCount) {
          uploads.splice(maxItemCount);
          nextTokenForReturn = null;
          break;
        }
        if (maxPageCount && pageCount >= maxPageCount) {
          break;
        }
      } while (nextToken);

      return { uploads, details, nextToken: nextTokenForReturn };
    } catch (error) {
      console.error('Error fetching bulk upload list with details:', error);
      throw error;
    }
  };

  const generateBulkUploadArchive = async ({ uploadId }) => {
    if (!uploadId) {
      throw new Error('uploadId is required.');
    }

    try {
      const response = await client.graphql({
        query: GENERATE_BULK_UPLOAD_ARCHIVE_MUTATION,
        authMode: 'userPool',
        variables: {
          id: uploadId,
        },
      });

      const archive = response?.data?.generateBulkUploadArchive;
      if (!archive?.downloadUrl || !archive?.fileName) {
        throw new Error('Bulk upload archive missing from response.');
      }

      return archive;
    } catch (error) {
      console.error('Error generating bulk upload archive:', error);
      throw error;
    }
  };

  return (
    <BulkUploadContext.Provider
      value={{
        uploadFile,
        listUploads,
        getConfiguration,
        getBulkUploadDetails,
        generateBulkUploadArchive,
        listUploadsWithDetails,
      }}
    >
      {children}
    </BulkUploadContext.Provider>
  );
};

export default BulkUploadContext;
